import { Button, Drawer, Flex } from 'antd';
import { ModifierContext } from 'containers/MenuManagement/context';
import useIsMobile from 'hooks/useIsMobile';
import { useParams } from 'react-router-dom';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { captureException } from 'utils/errors';
import useFetchedData from 'hooks/useFetchedData';
import Loading from 'components/Loading';
import {
  collectModifierOptionIds,
  isGlobalLinkedItem,
  normalizeModifierOptionRecipesFromApi,
  resolveAttachedModifiers,
  serializeModifierOptionRecipes,
  validateModifierOptionRecipes,
} from 'utils/menu';
import { ModalMode } from '../../../../constants';
import { MenuContext } from '../../../context';
import { addItem, editItem, updateItemImage } from '../requests';
import ItemForm from './ItemForm';

const initialValue = {
  name: '',
  isActive: true,
  sizes: [{ price: 0, serving_size: 1, name: '' }],
  tax: null,
  description: '',
  image: '',
  modifiers: [],
  recipeLines: [],
  modifierOptionRecipes: [],
};

function ItemDetailModal({
  mode = ModalMode.CLOSED, onClose, itemDetail: itemDetailProp, category,
}) {
  const { id: merchantId } = useParams();
  const { fetchMenuDetail, showMessage } = useContext(MenuContext);
  const { merchant, modifiers: allModifiers } = useContext(ModifierContext);
  const [detail, setDetail] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const {
    data: { data: itemDetail } = {},
    loading: itemLoading,
    mutate: refreshItemDetail,
  } = useFetchedData(itemDetailProp?.id && `/v1/items/${itemDetailProp.id}/`);

  useEffect(() => {
    if (mode === ModalMode.EDIT && itemDetailProp?.id) {
      refreshItemDetail?.();
    }
  }, [mode, itemDetailProp?.id, refreshItemDetail]);

  useEffect(() => {
    if (mode === ModalMode.NEW) setDetail(initialValue);
    if (mode === ModalMode.EDIT && itemDetail) {
      setDetail({
        name: itemDetail.name,
        description: itemDetail.description,
        image: (itemDetail.images.length > 0 ? itemDetail.images[0] : itemDetail.image) || '',
        isActive: itemDetail.is_active,
        tax: itemDetail.tax_ids?.[0],
        modifiers: itemDetail.modifiers?.filter((m) => m.options_parents?.length === 0) || [],
        recipeLines: itemDetail.recipe_lines || [],
        modifierOptionRecipes: normalizeModifierOptionRecipesFromApi(itemDetail.modifier_option_recipes),
        itemModifiers: resolveAttachedModifiers(
          itemDetail.modifiers?.filter((m) => m.options_parents?.length === 0) || [],
          allModifiers,
        ),
        cateringLabel: itemDetail.catering_label,
        dietaryTags: itemDetail.dietary_tags,
        sizes: itemDetail.sizes,
      });
    }
  }, [mode, itemDetail, allModifiers]);

  const modifierRecipeValidation = useMemo(
    () => validateModifierOptionRecipes(detail.modifierOptionRecipes, detail.recipeLines),
    [detail.modifierOptionRecipes, detail.recipeLines],
  );

  const saveDisabled = merchant?.is_catering
    && !isGlobalLinkedItem(itemDetail?.migration_driver)
    && !modifierRecipeValidation.valid;

  const onSubmit = () => {
    if (saveDisabled) {
      showMessage({
        type: 'error',
        content: modifierRecipeValidation.errors[0] || 'Fix modifier option recipes before saving.',
      });
      return;
    }

    const data = {
      name: detail.name,
      restaurant: merchantId,
      is_active: detail.isActive,
      description: detail.description,
      category: itemDetail?.category || itemDetail?.category_ids || [category.id],
      tax_ids: detail.tax ? [detail.tax] : [],
      cascade_modifiers_ids: [...new Set(detail.modifiers?.map((m) => m.id))],
      catering_label: merchant.is_catering ? detail.cateringLabel || 'item' : '',
      dietary_tags: detail.dietaryTags,
      sizes: detail.sizes.filter(({ price, name }, index) => price !== null && (index === 0 || name)),
    };

    if (!isGlobalLinkedItem(itemDetail?.migration_driver)) {
      data.recipe_lines = (detail.recipeLines || [])
        .filter((line) => line?.ingredient && Number(line?.quantity) > 0)
        .map((line) => ({
          ingredient: line.ingredient,
          quantity: line.quantity,
        }));
      const serializedRecipes = serializeModifierOptionRecipes(
        detail.modifierOptionRecipes,
        detail.recipeLines,
      );
      const serializedOptionIds = new Set(serializedRecipes.map((entry) => entry.modifier_option));
      const itemModifiersForRecipes = detail.itemModifiers?.length
        ? detail.itemModifiers
        : resolveAttachedModifiers(detail.modifiers, allModifiers);
      collectModifierOptionIds(itemModifiersForRecipes, allModifiers).forEach((optionId) => {
        if (!serializedOptionIds.has(optionId)) {
          serializedRecipes.push({ modifier_option: optionId, recipe_lines: [] });
        }
      });
      data.modifier_option_recipes = serializedRecipes;
    }

    setLoading(true);
    (mode === ModalMode.NEW ? addItem(data) : editItem({ id: itemDetail.id, data })).then((res) => {
      if (detail.image.id) {
        updateItemImage({ id: detail.image.id, data: { action: 'ADD', type: 'ITEM', id: mode === ModalMode.NEW ? res.id : itemDetail.id } })
          .then((res) => {
            onClose();
            fetchMenuDetail();
            setLoading(false);
          }).catch((e) => {
            captureException(e);
            showMessage({ type: 'error', content: 'Something went wrong on uploading image! please try again' });
            setLoading(false);
          });
      } else {
        onClose();
        fetchMenuDetail();
        setLoading(false);
      }
    }).catch((e) => {
      captureException(e);
      showMessage({ type: 'error' });
      setLoading(false);
    });
  };

  return (
    <Drawer
      title={`${mode === ModalMode.NEW ? 'Add' : 'Edit'} Item`}
      placement="right"
      width={isMobile ? '100%' : 575}
      onClose={onClose}
      open={mode !== ModalMode.CLOSED}
      footer={(
        <Flex justify="end" gap={8}>
          <Button
            loading={loading}
            type="primary"
            disabled={saveDisabled}
            onClick={onSubmit}
          >
            Save
          </Button>
        </Flex>
      )}
    >
      {
        itemLoading ? <Loading /> : (
          <ItemForm
            detail={detail}
            setDetail={setDetail}
            migrationDriver={itemDetail?.migration_driver}
            itemModifiers={detail.itemModifiers}
          />
        )
      }
    </Drawer>
  );
}

export default ItemDetailModal;
